/**
 * Demo owner directory for new-reservation tree (dashboard / Storybook).
 * Replace with API-driven list when backend exists.
 *
 * @typedef {{ id: string, name: string, email?: string, phone?: string, pets?: string[], fromNewPersonForm?: boolean }} OwnerDirectoryRow
 */

/** @type {OwnerDirectoryRow[]} */
export const DEFAULT_NEW_RESERVATION_OWNER_DIRECTORY = [
  {
    id: 'owner-as',
    name: 'Andrea Simmons',
    phone: '(510) 555-0142',
    email: 'andrea.simmons@berkeley.edu',
    pets: ['Berry', 'Porkchop', 'Tofu'],
  },
  {
    id: 'owner-ag',
    name: 'Anderson Gonzalez',
    email: 'anderson.gonzalez@example.com',
    pets: ['Milo', 'Luna'],
  },
  {
    id: 'owner-ap',
    name: 'Andharka Patelkhe',
    email: 'andharka.patelkhe@example.com',
    pets: ['Kiwi'],
  },
  {
    id: 'owner-vp',
    name: 'Victoria Pelham',
    phone: '+1 415-555-0199',
    email: 'john.doe@example.com',
    pets: ['Gotti', 'Capone'],
  },
  {
    id: 'owner-mr',
    name: 'Maria Rivera',
    email: 'maria.rivera@example.com',
    pets: ['Coco'],
  },
  {
    id: 'owner-jw',
    name: 'Jacqueline Wokeno',
    email: 'jacqueline.wokeno@example.com',
    pets: ['Bailey'],
  },
  {
    id: 'owner-ml',
    name: 'Morgan Lee',
    email: 'morgan.lee@example.com',
    pets: ['Scout', 'River'],
  },
  {
    id: 'owner-sd',
    name: 'Sam Dawson',
    email: 'sam.dawson@example.com',
    pets: ['Duke'],
  },
  {
    id: 'owner-ac',
    name: 'Alex Chen',
    email: 'alex.chen@example.com',
    pets: ['Noodle'],
  },
]

/**
 * @param {OwnerDirectoryRow[]} directory
 * @param {string} query
 * @param {{ max?: number }} [opts]
 */
function rowMatchesOwnerSearchToken(row, token) {
  const name = (row.name || '').toLowerCase()
  const email = (row.email || '').toLowerCase()
  const phone = (row.phone || '').toLowerCase()
  const petsJoined = (row.pets || []).join(' ').toLowerCase()
  return (
    name.includes(token) ||
    email.includes(token) ||
    phone.includes(token) ||
    petsJoined.includes(token) ||
    (row.pets || []).some((p) => p.toLowerCase().includes(token))
  )
}

/**
 * @param {string | null | undefined} ownerId
 * @param {OwnerDirectoryRow[]} [directory]
 * @returns {string | null}
 */
export function lookupOwnerDirectoryName(
  ownerId,
  directory = DEFAULT_NEW_RESERVATION_OWNER_DIRECTORY,
) {
  if (!ownerId || typeof ownerId !== 'string') {
    return null
  }
  const row = directory.find((r) => r.id === ownerId)
  const n = row?.name?.trim()
  return n || null
}

/**
 * Stream upserts often set `name` to the user id; treat that as “no display name”.
 * @param {{ id?: string, name?: string } | null | undefined} user
 * @returns {string | null}
 */
export function displayNameFromStreamUser(user) {
  if (!user?.id) {
    return null
  }
  const n = user.name?.trim()
  if (!n || n === user.id) {
    return null
  }
  return n
}

export function filterOwnerDirectory(directory, query, opts = {}) {
  const max = opts.max ?? 12
  const raw = (query || '').trim().toLowerCase()
  if (!raw) return directory.slice(0, max)

  if (raw.includes(',')) {
    const tokens = raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    if (!tokens.length) return directory.slice(0, max)
    return directory
      .filter((row) => tokens.some((t) => rowMatchesOwnerSearchToken(row, t)))
      .slice(0, max)
  }

  return directory
    .filter((row) => rowMatchesOwnerSearchToken(row, raw))
    .slice(0, max)
}
